import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePassageLimitsStore } from '@/stores/usePassageLimitsStore';

/**
 * Loads the public settings (passage limits, one-click menu visibility) for
 * unauthenticated pages; on authenticated pages LayoutBootstrap seeds them
 * from session-init first.
 *
 * The one-click menu flag answers for the signed-in account (a listed test
 * account sees the menu while the admin switch is Off), so a sign-in, sign-out
 * or account switch after the first load fetches the settings again.
 */
export function SettingsInitializer() {
  const fetchLimits = usePassageLimitsStore((s) => s.fetchLimits);
  const loaded = usePassageLimitsStore((s) => s.loaded);
  const { state } = useAuth();
  // Undefined until the stored session has been restored: that first account is the baseline, not a change.
  const userId = state.isLoading ? undefined : (state.user?.id ?? null);
  const settledUserIdRef = useRef<typeof userId>(undefined);

  useEffect(() => {
    // Skip fetch if LayoutBootstrap already seeded the store via session-init
    if (!loaded) {
      void fetchLimits();
    }
  }, [fetchLimits, loaded]);

  useEffect(() => {
    if (userId === undefined) return;
    if (settledUserIdRef.current !== undefined && settledUserIdRef.current !== userId) {
      void fetchLimits(true);
    }
    settledUserIdRef.current = userId;
  }, [fetchLimits, userId]);

  return null;
}
